import { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import {
  NumberField,
  PrecomputeFormulas,
  TextCollectionFieldType,
} from '@scale/shared/types/params/textcollection';
import {
  weighted_average,
  weighted_average_diff,
} from '@scale/shared/types/params/numberFieldFormulas';
import {
  NumberFieldAnnotation,
  TextCollectionAnnotation,
} from '@scale/shared/types/response/textcollection';
import { Slider } from 'design-system';
import { makeStyles } from 'design-system/styles';
import { getHelperText, getSectionTitle } from './helperText';
/* eslint-disable restrict-imports/restrict-imports */
import { FormHelperText } from '@material-ui/core';
import clsx from 'clsx';
import { useLLMHelper } from 'react-scale/dashboard.scale.com/contexts/LLMHelperContext';
import OpenRubricButton from './OpenRubricButton';

interface SliderFieldSectionProps {
  field: NumberField;
  annotation: NumberFieldAnnotation;
  setAnnotation: (annotation: NumberFieldAnnotation) => void;
  currentAnnotations?: Record<string, TextCollectionAnnotation>;
  disabled?: boolean;
}

export default function SliderFieldSection(props: SliderFieldSectionProps) {
  const onChange = function (index: number, value: string): void {
    const response = props.annotation.response.slice();
    response[index] = value;
    const annotation: NumberFieldAnnotation = {
      ...props.annotation,
      response,
    };
    props.setAnnotation(annotation);
  };
  const classes = useStyles();
  const marks = _.range(
    props.field.min || 0,
    (props.field.max || 100) + 1,
    props.field.step_ticks_override || props.field.step,
  ).map(num => {
    return {
      value: num,
      label: num,
    };
  });

  const llmHelper = useLLMHelper();
  const [aiAssistant, setAiAssistant] = useState<string>('');
  const [aiAssistantLoading, setAiAssistantLoading] = useState<boolean>(false);
  const [precomputeDefaultValue, setPrecomputeDefaultValue] = useState<number | undefined>(
    undefined,
  );

  const validPrecomputeProps = useCallback((props: SliderFieldSectionProps) => {
    if (
      !props.currentAnnotations ||
      !props.field.precompute_field_params ||
      !props.field.precompute_field_params.weights ||
      !props.field.precompute_field_params.precompute_formula ||
      !props.field.min ||
      !props.field.max
    )
      return false;

    const precomputeFormula = props.field.precompute_field_params.precompute_formula;
    const weightsMapList = props.field.precompute_field_params.weights;
    if (precomputeFormula === PrecomputeFormulas.WEIGHTED_AVERAGE && weightsMapList.length !== 1) {
      return false;
    } else if (precomputeFormula === PrecomputeFormulas.WEIGHTED_AVERAGE_DIFF) {
      if (weightsMapList.length !== 2) return false;
      if (!props.field.precompute_field_params.thresholds) return false;
    }
    return true;
  }, []);

  useEffect(() => {
    if (!validPrecomputeProps(props)) return;
    const precomputeFieldParams = props.field.precompute_field_params;
    const precomputeFormula = precomputeFieldParams?.precompute_formula;
    const weightsMapList = precomputeFieldParams?.weights!;
    const thresholds = precomputeFieldParams?.thresholds;

    const valuesList = [];
    const weightsList = [];

    for (const fieldWeightsMap of weightsMapList) {
      const values = [];
      const weights = [];
      for (const [fieldId, weight] of Object.entries(fieldWeightsMap)) {
        const annotation = props.currentAnnotations![fieldId];
        if (
          !annotation ||
          !annotation.response ||
          annotation.type !== TextCollectionFieldType.Number
        ) {
          return;
        }
        values.push(+annotation.response[0]);
        weights.push(weight);
      }
      valuesList.push(values);
      weightsList.push(weights);
    }

    if (precomputeFormula === PrecomputeFormulas.WEIGHTED_AVERAGE) {
      setPrecomputeDefaultValue(weighted_average(weightsList[0], valuesList[0]));
    } else if (precomputeFormula === PrecomputeFormulas.WEIGHTED_AVERAGE_DIFF) {
      setPrecomputeDefaultValue(
        weighted_average_diff({
          weightsA: weightsList[0],
          valuesA: valuesList[0],
          weightsB: weightsList[1],
          valuesB: valuesList[1],
          thresholds: thresholds!,
          minVal: props.field.min!,
          maxVal: props.field.max!,
        }),
      );
    }
  }, [
    precomputeDefaultValue,
    props,
    props.currentAnnotations,
    props.field.max,
    props.field.min,
    props.field.precompute_field_params,
    validPrecomputeProps,
  ]);

  useEffect(() => {
    if (
      llmHelper.aiAssistantEnabled &&
      Object.keys(llmHelper.aiAssistantPrompts).includes(props.field.field_id)
    ) {
      (async () => {
        try {
          setAiAssistantLoading(true);
          const content = await llmHelper.getAiAssistantResponse(props.field.field_id);
          if (content) setAiAssistant(content);
        } finally {
          setAiAssistantLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [llmHelper.aiAssistantEnabled, llmHelper.aiAssistantPrompts, props.field.field_id]);

  const helperText = getHelperText({ description: props.field.description });

  const labelsOverride = (props.field.labels_list_override ?? '').split(',').map(a => a.trim());

  const textFields = props.annotation.response.map((response: string, index: number) => {
    return (
      <div key={`response-${index}`}>
        <div className={classes.title} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {getSectionTitle(props.field.title)}

            {props.field.rubric && <OpenRubricButton rubric={props.field.rubric} />}
          </div>

          {helperText && (
            <FormHelperText classes={{ root: classes.helperText }}>{helperText}</FormHelperText>
          )}
        </div>
        {(!!aiAssistant.length || aiAssistantLoading) && (
          <div className={classes.aiAssistant}>
            {aiAssistantLoading && !aiAssistant.length
              ? 'AI assistant evaluation is loading...'
              : `The AI assistant suggests: ${aiAssistant}`}
          </div>
        )}
        {props.field.labels_list_override ? (
          <div className={classes.prefixSuffixRow}>
            {labelsOverride.map((l, i) => (
              <div
                key={l}
                className={clsx(
                  classes.valueDisplay,
                  i > (labelsOverride.length - 1) / 2
                    ? classes.textAlignEnd
                    : i === (labelsOverride.length - 1) / 2
                    ? classes.textAlignCenter
                    : undefined,
                )}
              >
                {l}
              </div>
            ))}
          </div>
        ) : (
          <div key={`prefix-suffix${index}`} className={classes.prefixSuffixRow}>
            <div className={classes.valueDisplay}>{props.field.prefix ?? ''}</div>
            <div className={clsx(classes.valueDisplay, classes.textAlignCenter)}>
              {props.field.mid_label ?? ''}
            </div>
            <div className={clsx(classes.valueDisplay, classes.textAlignEnd)}>
              {props.field.suffix ?? ''}
            </div>
          </div>
        )}
        <div key={index} className={classes.row}>
          <div className={classes.slider}>
            <Slider
              disabled={props.disabled}
              className={classes.sliderRoot}
              position={
                response.length === 0
                  ? validPrecomputeProps(props)
                    ? precomputeDefaultValue ?? props.field.min
                    : undefined
                  : +response
              }
              onAdjust={(position: number) => onChange(index, position.toString())}
              minVal={props.field.min}
              maxVal={props.field.max}
              step={props.field.step}
              marks={marks}
              valueLabelDisplay="auto"
              vertical={false}
            />
          </div>
        </div>
      </div>
    );
  });
  return <>{textFields}</>;
}

const useStyles = makeStyles({
  title: {
    color: '#000000',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    marginBottom: '4px',
    display: 'inline-block',
    alignItems: 'center',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  prefixSuffixRow: {
    display: 'flex',
    flexDirection: 'row',
    width: 'inherit',
    justifyContent: 'space-between',
    padding: 4,
    gap: 32,
    overflow: 'auto',
  },
  valueDisplay: {
    fontSize: '12px',
    width: '30%',
    padding: '2px',
    margin: '2px',
  },
  textAlignCenter: {
    textAlign: 'center',
  },
  textAlignEnd: {
    textAlign: 'end',
  },
  helperText: {
    marginBottom: '2px',
  },
  slider: {
    marginTop: 4,
    marginRight: 8,
    marginLeft: 8,
    marginBottom: 4,
    flexGrow: 1,
    paddingBottom: 16,
  },
  sliderRoot: {
    '@global .MuiSlider-root': {
      paddingBottom: '10px',
    },
  },
  aiAssistant: {
    marginBottom: 8,
    padding: 8,
    whiteSpace: 'pre-wrap',
    backgroundColor: 'rgba(144, 202, 249, .2)',
    borderRadius: 8,
  },
});
